/**
 * Pointer + keyboard input. Touch-first (Pillar 8): every pointer type goes
 * through tap/drag discrimination; hover affordances attach only when the
 * device actually hovers. Touch placement uses the ghost-then-confirm flow
 * from 10-presentation-and-ux.md; mouse placement stays single-click.
 */
import { CELL, NX, NY, SPEEDS } from '../data/balance.js';
import type { Game } from '../core/game.js';
import type { ViewState } from '../render/viewState.js';
import { hideTip, updateTip } from './tooltip.js';

export interface InputCallbacks {
  game(): Game;
  onPlace(x: number, y: number): void;
  onPickup(x: number, y: number): boolean;
  onLift(x: number, y: number): boolean;
  onDrop(x: number, y: number): boolean;
  onKeep(i: number): void;
  /** Socket the pending slate into the rock at (x, y); false if ineligible. */
  onSocket(x: number, y: number): boolean;
  /** Tap on a slate cell (greater-combine selection flow); false = untouched. */
  onSlateTap(x: number, y: number): boolean;
  onSpeed(idx: number): void;
  /** Pan the board by CSS pixels (touch drag). */
  onPan(dx: number, dy: number): void;
  /** Pinch gesture crossed a zoom threshold. */
  onZoom(mode: 'readable' | 'overview'): void;
}

const TAP_SLOP_PX = 9;
const LONG_PRESS_MS = 500;

export class Input {
  readonly hoverCapable = window.matchMedia('(hover: hover)').matches;

  private pointerDown: { id: number; sx: number; sy: number; moved: boolean } | null = null;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pinchStartDist: number | null = null;
  private pinching = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly view: ViewState,
    private readonly cb: InputCallbacks,
  ) {
    canvas.addEventListener('pointermove', (e) => {
      this.onPointerMove(e);
    });
    canvas.addEventListener('pointerleave', () => {
      this.view.hoverCell = null;
      this.view.mouseCanvas = null;
      hideTip();
    });
    canvas.addEventListener('pointerdown', (e) => {
      this.onPointerDown(e);
    });
    canvas.addEventListener('pointerup', (e) => {
      this.onPointerUp(e);
    });
    canvas.addEventListener('pointercancel', (e) => {
      this.pointers.delete(e.pointerId);
      if (this.pointers.size === 0) {
        this.pinching = false;
        this.pinchStartDist = null;
      }
      this.resetPointer();
    });
    document.addEventListener('keydown', (e) => {
      this.onKey(e);
    });
  }

  private canvasPos(e: PointerEvent): { mx: number; my: number } {
    // Logical board space (NX*CELL x NY*CELL) -- the backing store scales
    // with devicePixelRatio, so it must not appear in input math.
    const r = this.canvas.getBoundingClientRect();
    return {
      mx: (e.clientX - r.left) * ((NX * CELL) / r.width),
      my: (e.clientY - r.top) * ((NY * CELL) / r.height),
    };
  }

  private cellAt(e: PointerEvent): { x: number; y: number } | null {
    const { mx, my } = this.canvasPos(e);
    const x = Math.floor(mx / CELL);
    const y = Math.floor(my / CELL);
    return x >= 0 && y >= 0 && x < NX && y < NY ? { x, y } : null;
  }

  private pinchDistance(): number {
    const [a, b] = [...this.pointers.values()];
    return Math.hypot(a!.x - b!.x, a!.y - b!.y);
  }

  private onPointerMove(e: PointerEvent): void {
    if (this.pointers.has(e.pointerId)) {
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (this.pinching && this.pointers.size >= 2 && this.pinchStartDist !== null) {
      const d = this.pinchDistance();
      const ratio = d / this.pinchStartDist;
      if (ratio > 1.25) {
        this.cb.onZoom('readable');
        this.pinchStartDist = d;
      } else if (ratio < 0.8) {
        this.cb.onZoom('overview');
        this.pinchStartDist = d;
      }
      return;
    }
    if (this.pointerDown && e.pointerId === this.pointerDown.id) {
      const dx = e.clientX - this.pointerDown.sx;
      const dy = e.clientY - this.pointerDown.sy;
      if (this.pointerDown.moved || Math.hypot(dx, dy) > TAP_SLOP_PX) {
        if (!this.pointerDown.moved) this.pointerDown.moved = true;
        else if (e.pointerType !== 'mouse') this.cb.onPan(e.movementX, e.movementY);
        this.cancelLongPress();
      }
    }
    if (this.hoverCapable) {
      const { mx, my } = this.canvasPos(e);
      this.view.mouseCanvas = { mx, my };
      this.view.hoverCell = this.cellAt(e);
      updateTip(this.cb.game(), this.view, e.clientX, e.clientY);
    }
  }

  private onPointerDown(e: PointerEvent): void {
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.pointers.size === 2) {
      // Second finger down: this is a pinch, not a tap.
      this.pinching = true;
      this.pinchStartDist = this.pinchDistance();
      this.pointerDown = null;
      this.cancelLongPress();
      return;
    }
    this.pointerDown = { id: e.pointerId, sx: e.clientX, sy: e.clientY, moved: false };
    if (e.pointerType !== 'mouse') {
      const cell = this.cellAt(e);
      if (cell) {
        this.longPressTimer = setTimeout(() => {
          // Long-press: pick a fresh stone back up, or lift a settled piece
          // (touch equivalent of the desktop click flows).
          if (!this.view.replayMode) {
            if (this.cb.onPickup(cell.x, cell.y) || this.cb.onLift(cell.x, cell.y)) {
              this.view.pendingPlace = null;
            }
          }
          this.pointerDown = null;
        }, LONG_PRESS_MS);
      }
    }
  }

  private onPointerUp(e: PointerEvent): void {
    this.pointers.delete(e.pointerId);
    if (this.pinching) {
      if (this.pointers.size === 0) {
        this.pinching = false;
        this.pinchStartDist = null;
      }
      this.resetPointer();
      return;
    }
    const down = this.pointerDown;
    this.resetPointer();
    if (!down || down.id !== e.pointerId || down.moved) return; // drag, not tap
    const cell = this.cellAt(e);
    if (!cell || this.view.replayMode) return;
    const game = this.cb.game();
    const gridCell = game.grid[cell.y]![cell.x];

    // Slate socket picker intercepts every tap: an eligible rock sockets the
    // pending forge, anything else falls through to the normal flows.
    if (this.view.pendingSlate && this.cb.onSocket(cell.x, cell.y)) return;
    // Slate taps drive the greater-combine selection (both pointer types).
    if (gridCell?.kind === 'slate' && this.cb.onSlateTap(cell.x, cell.y)) return;
    if (this.view.pendingSlateUp && gridCell?.kind !== 'slate') this.view.pendingSlateUp = null;

    if (e.pointerType === 'mouse') {
      // Holding a lifted piece: any empty cell drops it.
      if (game.lifted) {
        if (!gridCell) this.cb.onDrop(cell.x, cell.y);
        return;
      }
      // Click picks up a fresh stone, lifts a settled piece (build phase),
      // else places.
      if (this.cb.onPickup(cell.x, cell.y)) return;
      if (game.phase === 'build' && gridCell) {
        if (this.cb.onLift(cell.x, cell.y)) return;
      }
      if (game.phase === 'build' && !gridCell) this.cb.onPlace(cell.x, cell.y);
      return;
    }

    // Touch: ghost first, confirm on the second tap of the same cell.
    // While holding a lifted piece the confirm drops it instead of placing.
    const holding = game.lifted !== null;
    if (
      this.view.pendingPlace &&
      this.view.pendingPlace.x === cell.x &&
      this.view.pendingPlace.y === cell.y
    ) {
      this.view.pendingPlace = null;
      if (!gridCell) {
        if (holding) this.cb.onDrop(cell.x, cell.y);
        else if (game.phase === 'build') this.cb.onPlace(cell.x, cell.y);
      }
      return;
    }
    if (!gridCell && (holding || game.phase === 'build')) {
      this.view.pendingPlace = cell;
      return;
    }
    // Tap on an occupied cell: inspect it.
    this.view.pendingPlace = null;
    this.view.hoverCell = cell;
    const { mx, my } = this.canvasPos(e);
    this.view.mouseCanvas = { mx, my };
    updateTip(game, this.view, e.clientX, e.clientY);
  }

  private onKey(e: KeyboardEvent): void {
    if (e.target instanceof HTMLInputElement) return;
    if (e.key === 'Escape') {
      this.view.pendingSlate = null;
      this.view.pendingSlateUp = null;
      this.view.pendingPlace = null;
      return;
    }
    if (e.key >= '1' && e.key <= '5' && !this.view.replayMode) {
      this.cb.onKeep(Number(e.key) - 1);
    }
    const speedKeys: Record<string, number> = { q: 0, w: 1, e: 2, r: 3, t: 4 };
    const idx = speedKeys[e.key];
    if (idx !== undefined && idx < SPEEDS.length) this.cb.onSpeed(idx);
  }

  private cancelLongPress(): void {
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  private resetPointer(): void {
    this.pointerDown = null;
    this.cancelLongPress();
  }
}
